import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  getProjectArtifactRowId,
  type ProjectArtifactsGridSelection,
} from '../components/artifact-selection/selection';
import type { ProjectArtifact } from '../types';

// Keeps one request inside the LB idle timeout. Enforced again server-side.
export const MAX_EXPORT_CONCEPT_GROUPS = 50;

/** Drafts live in the DynamoDB preview store and have no concepts to export. */
const isReadyConceptGroup = (artifact: ProjectArtifact): boolean =>
  artifact.kind === 'concept_group' && artifact.assetStatus === 'Ready';

export interface UseConceptGroupExportSelectionParams {
  /** The whole manifest — a selected row id maps back to an artifact through it. */
  artifacts: ProjectArtifact[];
  /** What the grid lists right now, which is what select-all would take. */
  visibleArtifacts: ProjectArtifact[];
  /** False parks the hook and drops any selection. The caller passes the active
   *  filter through this. */
  enabled: boolean;
}

export interface ConceptGroupExportSelection {
  /** Ready concept-group ids behind the current selection, in manifest order. */
  selectedIds: string[];
  selectedCount: number;
  /** Ready concept groups the current list offers — what select-all draws from. */
  selectableCount: number;
  gridSelection: ProjectArtifactsGridSelection;
  clear: () => void;
}

/**
 * Checkbox selection, capped at `MAX_EXPORT_CONCEPT_GROUPS` (OR-6580). The cap
 * bites where the user meets it — rows stop offering a checkbox — not at submit.
 */
export const useConceptGroupExportSelection = ({
  artifacts,
  visibleArtifacts,
  enabled,
}: UseConceptGroupExportSelectionParams): ConceptGroupExportSelection => {
  const { formatMessage, locale } = useIntl();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  // Idempotent, so the effect below can call it without forcing a render.
  const clear = useCallback(() => {
    setSelectionModel((current) => (current.length ? [] : current));
  }, []);

  // Leaving the tab abandons the export: a selection kept alive behind another
  // filter would be invisible state that reappears on the way back.
  useEffect(() => {
    if (!enabled) clear();
  }, [enabled, clear]);

  const toggleRow = useCallback((rowId: string, checked: boolean) => {
    setSelectionModel((current) =>
      checked ? [...current, rowId] : current.filter((id) => id !== rowId)
    );
  }, []);

  // Shared with the grid, so it does not rebuild the same set.
  const selectedRowIds = useMemo(() => new Set(selectionModel), [selectionModel]);

  const selectedIds = useMemo(
    () =>
      artifacts
        .filter((a) => isReadyConceptGroup(a) && selectedRowIds.has(getProjectArtifactRowId(a)))
        .map((a) => a.id),
    [artifacts, selectedRowIds]
  );
  const selectedCount = selectedIds.length;

  // Keyed on `locale`, not `formatMessage` — that is a fresh function every render,
  // and depending on it would rebuild the grid's whole column array each time.
  const refusalMessages = useMemo(
    () => ({
      notReady: formatMessage({ id: 'conceptGroups.export.rowNotReady' }),
      capReached: formatMessage(
        { id: 'conceptGroups.export.selectionLimitReached' },
        { max: MAX_EXPORT_CONCEPT_GROUPS }
      ),
    }),
    [locale]
  );

  // Counted off `visibleArtifacts`, so narrowing with search changes what
  // select-all takes.
  const listedReady = useMemo(
    () => visibleArtifacts.filter(isReadyConceptGroup),
    [visibleArtifacts]
  );

  // Over-cap, select-all takes a capful rather than going inert — a control the
  // user cannot press teaches nothing, and the pill says what it took.
  const selectAll = useMemo(() => {
    const readyRowIds = new Set(listedReady.map(getProjectArtifactRowId));
    // Whether select-all has anything left to add, which is what the header's tick
    // claims. Over the cap that is nothing, however few of the listed rows are in.
    const allSelected =
      readyRowIds.size > 0 &&
      (selectedCount >= MAX_EXPORT_CONCEPT_GROUPS ||
        [...readyRowIds].every((id) => selectedRowIds.has(id)));

    return {
      allSelected,
      apply:
        enabled && readyRowIds.size > 0 && !allSelected
          ? (displayedRowIds: readonly GridRowId[]) => {
              // The grid's order when it has one: over the cap this picks which rows
              // get taken, and taking any order but the one on screen puts the ticks
              // somewhere the user cannot account for.
              const ordered = displayedRowIds.length
                ? displayedRowIds.filter((id) => readyRowIds.has(String(id)))
                : [...readyRowIds];
              setSelectionModel(ordered.slice(0, MAX_EXPORT_CONCEPT_GROUPS));
            }
          : undefined,
    };
  }, [enabled, listedReady, selectedRowIds, selectedCount]);

  const gridSelection = useMemo<ProjectArtifactsGridSelection>(
    () => ({
      active: enabled,
      model: selectionModel,
      selectedRowIds,
      toggleRow,
      // Anything that is not a concept group gets no checkbox at all — a tooltip
      // about concept groups would answer a question the row never asked.
      getRowSelectability: (artifact) => {
        if (artifact.kind !== 'concept_group') return { selectable: false };
        if (selectedRowIds.has(getProjectArtifactRowId(artifact))) return { selectable: true };
        if (!isReadyConceptGroup(artifact)) {
          return { selectable: false, refusedReason: refusalMessages.notReady };
        }
        if (selectedCount >= MAX_EXPORT_CONCEPT_GROUPS) {
          return { selectable: false, refusedReason: refusalMessages.capReached };
        }
        return { selectable: true };
      },
      selectAll,
      clearAll: clear,
    }),
    [
      enabled,
      selectionModel,
      selectedRowIds,
      toggleRow,
      selectedCount,
      selectAll,
      clear,
      refusalMessages,
    ]
  );

  return { selectedIds, selectedCount, selectableCount: listedReady.length, gridSelection, clear };
};
